import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pause, Play } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Select, Table, Tag, TextArea } from '@voice/ui';
import { errorText, formatDuration } from '../components/common';
import { api, apiUpload } from '../lib/api';
import styles from './Pages.module.css';

interface AudioFile {
  id: string;
  name: string;
  kind: 'announcement' | 'music';
  source: 'upload' | 'tts' | 'recording';
  durationMs: number;
  ttsText: string | null;
  createdAt: string;
}
interface MohClass {
  id: string;
  name: string;
  files: string[];
  random: boolean;
}
interface AudioResponse {
  files: AudioFile[];
  classes: MohClass[];
  tts: { available: boolean; provider: string | null };
  stt: { available: boolean; provider: string | null };
}

const KEY = ['audio'] as const;
const SOURCE: Record<AudioFile['source'], string> = { upload: 'Hochgeladen', tts: 'Sprachausgabe', recording: 'Aufnahme' };

export function Audio() {
  const query = useQuery({ queryKey: KEY, queryFn: () => api<AudioResponse>('/audio') });
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'upload' | 'tts' | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const player = useRef<HTMLAudioElement | null>(null);
  const [moh, setMoh] = useState<MohClass | 'new' | null>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: KEY }), qc.invalidateQueries({ queryKey: ['directory'] })]);
  const remove = useMutation({ mutationFn: (id: string) => api(`/audio/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const rename = useMutation({ mutationFn: (v: { id: string; name: string }) => api(`/audio/${v.id}`, { method: 'PATCH', body: { name: v.name } }), onSuccess: refresh });
  const data = query.data;

  const toggle = (f: AudioFile) => {
    if (playing === f.id) {
      player.current?.pause();
      setPlaying(null);
      return;
    }
    player.current?.pause();
    const a = new window.Audio(`/api/audio/${f.id}/file`);
    a.onended = () => setPlaying(null);
    void a.play().catch(() => setPlaying(null));
    player.current = a;
    setPlaying(f.id);
  };

  return (
    <div className={styles.page}>
      <PageHeader title="Ansagen und Wartemusik">
        <Button variant="secondary" onClick={() => setDialog('upload')}>
          Datei hochladen
        </Button>
        <Button onClick={() => setDialog('tts')}>Ansage erstellen</Button>
      </PageHeader>

      {query.isPending && <p className={styles.note}>Lädt …</p>}
      {query.isError && <Notice tone="error">Die Ansagen konnten nicht geladen werden.</Notice>}
      {data && data.files.length === 0 && <EmptyState>Noch keine Ansagen. Lade eine Datei hoch (MP3, WAV, …) oder lass einen Text sprechen.</EmptyState>}
      {data && data.files.length > 0 && (
        <Table aria-label="Ansagen und Musik">
          <thead>
            <tr>
              <th />
              <th>Name</th>
              <th>Art</th>
              <th>Dauer</th>
              <th>Herkunft</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.files.map((f) => (
              <tr key={f.id}>
                <td>
                  <Button variant="secondary" onClick={() => toggle(f)} aria-label={playing === f.id ? 'Anhalten' : 'Abspielen'}>
                    {playing === f.id ? <Pause size={16} /> : <Play size={16} />}
                  </Button>
                </td>
                <th scope="row">
                  {f.name}
                  {f.ttsText && <div className={styles.muted}>{f.ttsText.length > 90 ? `${f.ttsText.slice(0, 90)} …` : f.ttsText}</div>}
                </th>
                <td>{f.kind === 'music' ? 'Musik' : 'Ansage'}</td>
                <td>{formatDuration(f.durationMs)}</td>
                <td>{SOURCE[f.source]}</td>
                <td>
                  <RowActions>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        const name = prompt('Neuer Name', f.name)?.trim();
                        if (name) rename.mutate({ id: f.id, name });
                      }}
                    >
                      Umbenennen
                    </Button>
                    <Button variant="secondary" onClick={() => confirm(`„${f.name}“ löschen?`) && remove.mutate(f.id)}>
                      Löschen
                    </Button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {remove.isError && <Notice tone="error">{errorText(remove.error)}</Notice>}

      {data && (
        <section className={styles.section}>
          <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
            <h2 className={styles.heading}>Wartemusik-Gruppen</h2>
            <Button variant="secondary" onClick={() => setMoh('new')}>
              Gruppe anlegen
            </Button>
          </div>
          <p className={styles.note} style={{ marginBottom: 12 }}>
            Eine Gruppe spielt mehrere Titel nacheinander. Wähle sie bei einer Warteschlange aus. Ohne eigene Gruppe läuft die Standard-Musik.
          </p>
          {data.classes.length === 0 ? (
            <p className={styles.muted}>Noch keine Gruppe.</p>
          ) : (
            <Table aria-label="Wartemusik-Gruppen">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Titel</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.classes.map((c) => (
                  <tr key={c.id}>
                    <th scope="row">{c.name}</th>
                    <td>
                      {c.files.length} {c.random && <Tag>zufällig</Tag>}
                    </td>
                    <td>
                      <RowActions>
                        <Button variant="secondary" onClick={() => setMoh(c)}>
                          Bearbeiten
                        </Button>
                      </RowActions>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      {data && (
        <section className={styles.section}>
          <h2 className={styles.heading}>Sprache</h2>
          <p className={styles.note}>
            Sprachausgabe: {data.tts.available ? `aktiv (${data.tts.provider})` : 'nicht eingerichtet'} · Spracherkennung: {data.stt.available ? `aktiv (${data.stt.provider})` : 'nicht eingerichtet'}
          </p>
          {(!data.tts.available || !data.stt.available) && (
            <p className={styles.muted}>Einrichtung über die Umgebungsvariablen TTS_* und STT_* (siehe README). Ohne sie bleiben Ansagen aus Dateien nutzbar; das virtuelle Sekretariat kann dann aber nichts sprechen oder verstehen.</p>
          )}
        </section>
      )}

      {dialog === 'upload' && <UploadForm onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog === 'tts' && data && <TtsForm available={data.tts.available} onClose={() => setDialog(null)} onDone={refresh} />}
      {moh && data && <MohForm cls={moh === 'new' ? null : moh} files={data.files} onClose={() => setMoh(null)} onDone={refresh} />}
    </div>
  );
}

function UploadForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'announcement' | 'music'>('announcement');
  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('name', name || file!.name.replace(/\.[^.]+$/, ''));
      form.append('kind', kind);
      form.append('file', file!);
      return apiUpload('/audio/upload', form);
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Datei hochladen"
      description="MP3, WAV, M4A, OGG … bis 60 MB. Die Datei wird automatisch ins Telefonformat umgewandelt."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => upload.mutate()} disabled={!file || upload.isPending}>
            {upload.isPending ? 'Lädt hoch …' : 'Hochladen'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div>
          <label htmlFor="audio-file" className={styles.labelText}>
            Datei
          </label>
          <input
            id="audio-file"
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac,.aac"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
            }}
          />
        </div>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Select label="Art" value={kind} onChange={(e) => setKind(e.target.value as 'announcement' | 'music')}>
          <option value="announcement">Ansage</option>
          <option value="music">Musik</option>
        </Select>
        {upload.isError && <Notice tone="error">{errorText(upload.error)}</Notice>}
      </div>
    </Modal>
  );
}

function TtsForm({ available, onClose, onDone }: { available: boolean; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const create = useMutation({
    mutationFn: () => api('/audio/tts', { method: 'POST', body: { name, text } }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Ansage aus Text erstellen"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => create.mutate()} disabled={!available || !name || !text || create.isPending}>
            {create.isPending ? 'Erstellt …' : 'Erstellen'}
          </Button>
        </>
      }
    >
      <form className={styles.form} onSubmit={submit}>
        {!available && <Notice tone="error">Keine Sprachausgabe eingerichtet (TTS_PROVIDER). Du kannst stattdessen eine Datei hochladen.</Notice>}
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Begrüßung" autoFocus />
        <TextArea label="Text" value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Willkommen bei byliox. Bitte haben Sie einen Moment Geduld." />
        {create.isError && <Notice tone="error">{errorText(create.error)}</Notice>}
      </form>
    </Modal>
  );
}

function MohForm({ cls, files, onClose, onDone }: { cls: MohClass | null; files: AudioFile[]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(cls?.name ?? '');
  const [selected, setSelected] = useState<string[]>(cls?.files ?? []);
  const [random, setRandom] = useState(cls?.random ?? false);
  const save = useMutation({
    mutationFn: () => (cls ? api(`/moh/${cls.id}`, { method: 'PUT', body: { name, files: selected, random } }) : api('/moh', { method: 'POST', body: { name, files: selected, random } })),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/moh/${cls!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const sorted = [...files].sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'music' ? -1 : 1));
  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <Modal
      open
      onClose={onClose}
      title={cls ? `Wartemusik „${cls.name}“` : 'Wartemusik-Gruppe anlegen'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!name || selected.length === 0 || save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <fieldset className={styles.fieldset}>
          <legend className={styles.labelText}>Titel (in der Reihenfolge der Auswahl)</legend>
          <div className={styles.stack}>
            {sorted.map((f) => (
              <Checkbox key={f.id} label={`${f.name} (${formatDuration(f.durationMs)})${f.kind === 'music' ? '' : ' – Ansage'}`} checked={selected.includes(f.id)} onChange={() => toggle(f.id)} />
            ))}
            {sorted.length === 0 && <p className={styles.muted}>Lade zuerst Musik hoch.</p>}
          </div>
        </fieldset>
        <Checkbox label="Titel in zufälliger Reihenfolge spielen" checked={random} onChange={(e) => setRandom(e.target.checked)} />
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {cls && (
          <div>
            <Button variant="secondary" onClick={() => confirm('Gruppe löschen?') && remove.mutate()}>
              Gruppe löschen
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
